// GameAnalyzer.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GameAnalyzerChart from '../gameAnalyzer/Chart';
import ResultsOverview from '../gameAnalyzer/ResultsOverview';
import Dropdown from '../Assets/Dropdown';
import { ArrowLeftIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import apiService from '../../utils/apiService';

// Import sport-specific adapters
import { getSportAdapter } from './sportAdapers';

const GameAnalyzer = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const game = location.state?.game || null;
    const sport = location.state?.sport || null;
    
    // Get the sport-specific adapter
    const sportAdapter = getSportAdapter(sport);
    
    // Common state variables
    const [scoresData, setScoresData] = useState(null);
    const [chartType, setChartType] = useState(sportAdapter?.defaultChartType || null);
    const [subSelection, setSubSelection] = useState(sportAdapter?.defaultSubSelection || null);
    const [numGames, setNumGames] = useState(5);
    const [chartData, setChartData] = useState(null);
    const [chartOptions, setChartOptions] = useState({});
    const [chartLabel, setChartLabel] = useState('');
    const [horizontalLine, setHorizontalLine] = useState(null);
    const [subDropdownOptions, setSubDropdownOptions] = useState(null);
    const [apiError, setApiError] = useState(false);
    const [gamesData, setGamesData] = useState(null);
    const [totalsResults, setTotalsResults] = useState(null);
    const [spreadResults, setSpreadResults] = useState(null);
    const [results, setResults] = useState(null);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    // Common chart colors
    const chartColors = {
        homeColor: "rgba(66, 135, 245, 0.75)",
        homeBorderColor: "rgba(66, 135, 245, 1)",
        awayColor: 'rgba(255, 45, 85, .75)',
        awayBorderColor: 'rgba(255, 45, 85, 1)',
        winColor: 'rgba(52, 199, 89, .75)',
        winBorder: 'rgba(52, 199, 89, 1)'
    };

    // Fetch game scores data using the sport-specific endpoint
    const fetchGameScores = useCallback(async (game) => {
        try {
            const endpoint = sportAdapter.getScoresEndpoint(game);
            const response = await apiService(endpoint, 'GET', null, {});
            setScoresData(response);
        } catch (error) {
            if(error.status === 304) return;

            if(error.status === 401 || error.message === 'Session Expired') {
                navigate('/signin');
            } else if(error.status === 0) {
                console.error(`Network error while fetching ${sport} scores:`, error.message);
            } else {
                console.error(`Error fetching ${sport} scores (${error.status}):`, error.message);
            }

            setApiError(true);
        }
    }, [sport, sportAdapter, navigate]);

    // Initialize on component mount
    useEffect(() => {
        if(game === null || sport === null) {
            navigate('/upcoming-games');
        } else {
            fetchGameScores(game);
        }
    }, [game, sport, fetchGameScores, navigate]);

    // Process scores data to game data structure
    useEffect(() => {
        if(!scoresData || !game) return;
        
        const processedData = sportAdapter.processScoresData(scoresData);
        setGamesData(processedData.gamesData);
    }, [scoresData, game, sportAdapter]);

    // Calculate betting line results
    useEffect(() => {
        if(!gamesData || !game) return;
        
        const bettingLineResults = sportAdapter.calculateBettingResults(gamesData, game);
        setTotalsResults(bettingLineResults.totals);
        setSpreadResults(bettingLineResults.spread);
    }, [gamesData, game, sportAdapter]);

    // Helper functions for statistics
    const getAverages = useCallback((data) => {
        const calculateAverage = arr => (arr.reduce((sum, value) => sum + value, 0) / arr.length).toFixed(1);
        return [5, 10, 15, 20, 30].map(n => calculateAverage(data.slice(-n)));
    }, []);

    // Win/Loss Sets for Overview Section
    const getWLSets = useCallback((results) => {
        return [5, 10, 15, 20, 30].map(n => {
            let res = { w: 0, l: 0, p: 0 };
            const slice = results.slice(-n);
            
            for (let i = 0; i < slice.length; i++) {
                if (slice[i] === 1) res.w++;
                else if (slice[i] === 0) res.l++;
                else if (slice[i] === 2) res.p++;
            }
            
            const record = `${res.w}-${res.l}-${res.p}`;
            const winPer = res.w + res.l > 0 ? (res.w/(res.w + res.l)*100).toFixed(1) : "N/A";
            
            return { record, winPer };
        });
    }, []);

    // Generate results for various chart types
    const getResults = useCallback(() => {
        if (!gamesData) return;
        
        return sportAdapter.generateResults({
            chartType,
            subSelection,
            gamesData,
            totalsResults,
            spreadResults,
            getWLSets,
            getAverages
        });
    }, [subSelection, gamesData, totalsResults, spreadResults, sportAdapter, getWLSets, getAverages]);

    // Update results when selections change
    useEffect(() => {        
        const shouldUpdateResults = sportAdapter?.shouldUpdateResults({
            chartType,
            subSelection,
            gamesData,
            totalsResults,
            spreadResults,
            results,
            subDropdownOptions
        }) || false;
        
        
        if (shouldUpdateResults) {
            const newResults = getResults();
            setResults(newResults);
        }
    }, [chartType, subSelection, gamesData, totalsResults, spreadResults, 
        subDropdownOptions, sportAdapter, getResults]);

    // Get team colors for chart
    const getTeamColors = (team) => {
        const isHomeTeam = sport === 'mlb' 
            ? team === game?.home?.team
            : team === game?.home?.team_id;
            
        return isHomeTeam
            ? { color: chartColors.homeColor, border: chartColors.homeBorderColor }
            : { color: chartColors.awayColor, border: chartColors.awayBorderColor };
    };

    // Format chart data based on chart type
    const formatChartData = useCallback(() => {

        if (!gamesData) return null;
        
        const formatParams = {
            chartType,
            subSelection,
            numGames,
            gamesData,
            totalsResults,
            spreadResults,
            getTeamColors,
            chartColors,
            game,
            teamSelection: subSelection
        };
        
        return sportAdapter.formatChartData(formatParams);
    }, [chartType, subSelection, numGames, gamesData, totalsResults, spreadResults, 
        chartColors, sportAdapter, game]);

    // Update chart options when chart type changes
    useEffect(() => {
        const options = sportAdapter?.getSubDropdownOptions(chartType, game) || null;
        setSubDropdownOptions(options?.options || null);
        
        if(!options || !options.options){
            setSubSelection(null);
        }else if (options.defaultSelection && 
            (options.forceDefault || !subSelection || !options.options?.some(opt => opt.key === subSelection))) {
            setSubSelection(options.defaultSelection);
        }
    }, [chartType, game, sportAdapter]);

    // Update chart data when inputs change
    useEffect(() => {   
        const formattedData = formatChartData();
        if (formattedData) {
            setChartLabel(formattedData.chartLabel);
            setChartData(formattedData.chartData);
            setChartOptions(formattedData.chartOptions);
            setHorizontalLine(formattedData.horizontalLine);
        }
    }, [subSelection, numGames, gamesData, totalsResults, spreadResults]);

    // Change number of games to display
    const changeNumGames = (num) => {
        setNumGames(num);
        
        // Update UI classes for selected games range
        const oldElements = document.getElementsByClassName('selected');
        if (oldElements.length > 0) {
            Array.from(oldElements).forEach(element => {
                element.classList.remove('selected');
            });
        }
        
        const newElements = document.getElementsByClassName(`last${num}-games`);
        if (newElements.length > 0) {
            Array.from(newElements).forEach(element => {
                element.classList.add('selected');
            });
        }
    };

    return (
        <div className="max-h-screen bg-gray-50 dark:bg-brand-dark-900">
            {/* Mobile filter dialog */}
            <div className={`fixed inset-0 z-40 lg:hidden ${mobileFiltersOpen ? 'block' : 'hidden'}`}>
                {/* Mobile filter content */}
                <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setMobileFiltersOpen(false)}></div>
                <div className="fixed inset-y-0 left-0 z-40 w-full max-w-xs overflow-y-auto bg-white dark:bg-gray-900 p-6 shadow-xl transform transition-transform duration-300">
                    {/* Filter controls */}
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Analysis Controls</h2>
                        <button
                            type="button"
                            className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md p-2 text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-white"
                            onClick={() => setMobileFiltersOpen(false)}
                        >
                            <span className="sr-only">Close menu</span>
                            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Mobile filter options */}
                    <div className="space-y-6">
                        {/* Chart type dropdown */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Chart Type</h3>
                            {sportAdapter && (
                                <Dropdown
                                    title="Select Chart Type"
                                    items={sportAdapter.getChartTypeOptions()}
                                    activeItem={chartType}
                                    onSelect={setChartType}
                                    className="mt-2 w-full"
                                />
                            )}
                        </div>

                        {/* Sub-options dropdown if available */}
                        {subDropdownOptions && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white">Options</h3>
                                <Dropdown
                                    title="Select Option"
                                    items={subDropdownOptions.map(opt => ({
                                        value: opt.key,
                                        label: opt.value
                                    }))}
                                    activeItem={subSelection}
                                    onSelect={setSubSelection}
                                    className="mt-2 w-full"
                                />
                            </div>
                        )}

                        {/* Games range buttons */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Games Range</h3>
                            <div className="mt-2 flex flex-col gap-2">
                                {["L5", "L10", "L15", "L20", "L30"].map((label) => {
                                    const num = parseInt(label.slice(1));
                                    const isSelected = num === numGames;
                                    return (
                                        <button
                                            key={label}
                                            onClick={() => changeNumGames(num)}
                                            className={`flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                isSelected 
                                                    ? 'bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white' 
                                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                            }`}
                                        >
                                            {label} Games
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row min-h-screen">
                {/* Left sidebar (desktop) */}
                <aside className="hidden lg:block lg:sticky lg:top-0 lg:h-screen lg:w-72 lg:border-r lg:border-gray-200 dark:lg:border-gray-700 lg:overflow-y-auto bg-white dark:bg-brand-dark-900 p-4 shrink-0">
                    <div className="sticky top-0 space-y-6">
                        {/* Header with back button */}
                        <div className="pb-5 border-b border-gray-200 dark:border-gray-700">
                            <button 
                                onClick={() => navigate(`/upcoming-games`, {state:{game:game, sport:sport}})}
                                className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-4"
                            >
                                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                                Back to Game
                            </button>
                            <h1 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white">
                                {sport === 'mlb' ? game?.away?.team : game?.away?.team_id} @ {sport === 'mlb' ? game?.home?.team : game?.home?.team_id}
                            </h1>
                        </div>

                        {/* Desktop filter controls */}
                        {/* Chart Type Selection */}
                        <div>
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                    <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                                </div>
                                <div className="relative flex justify-start">
                                    <span className="bg-white dark:bg-brand-dark-900 pr-3 text-sm font-medium text-gray-900 dark:text-white">Chart Type</span>
                                </div>
                            </div>
                            <div className="mt-4">
                                {sportAdapter && (
                                    <Dropdown
                                        title="Select Chart Type"
                                        items={sportAdapter.getChartTypeOptions()}
                                        activeItem={chartType}
                                        onSelect={setChartType}
                                        className="w-full"
                                    />
                                )}
                            </div>
                        </div>

                        {/* Sub Options if available */}
                        {subDropdownOptions && (
                            <div>
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                        <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                                    </div>
                                    <div className="relative flex justify-start">
                                        <span className="bg-white dark:bg-brand-dark-900 pr-3 text-sm font-medium text-gray-900 dark:text-white">Options</span>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <Dropdown
                                        title="Select Option"
                                        items={subDropdownOptions.map(opt => ({
                                            value: opt.key,
                                            label: opt.value
                                        }))}
                                        activeItem={subSelection}
                                        onSelect={setSubSelection}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Games Range Selection */}
                        <div>
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                    <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                                </div>
                                <div className="relative flex justify-start">
                                    <span className="bg-white dark:bg-brand-dark-900 pr-3 text-sm font-medium text-gray-900 dark:text-white">Games Range</span>
                                </div>
                            </div>
                            <div className="space-y-2 mt-4">
                                {["L5", "L10", "L15", "L20", "L30"].map((label) => {
                                    const num = parseInt(label.slice(1));
                                    const isSelected = num === numGames;
                                    return (
                                        <button
                                            key={label}
                                            onClick={() => changeNumGames(num)}
                                            className={`flex w-full items-center justify-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                isSelected 
                                                    ? 'bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white' 
                                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                            }`}
                                        >
                                            {label} Games
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </aside>

                {/* Main content */}
                <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-brand-dark-900">
                    {/* Mobile header */}
                    <div className="lg:hidden sticky top-0 z-10 bg-white dark:bg-brand-dark-900 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
                        <div className="flex flex-col space-y-4">
                            <div className="flex items-center justify-between">
                                <button 
                                    onClick={() => navigate(`/upcoming-games`, {state:{game:game, sport:sport}})}
                                    className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                >
                                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                                    Back to Game
                                </button>
                            </div>
                            <button
                                type="button"
                                className="flex items-center justify-center gap-2 w-full p-2 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700"
                                onClick={() => setMobileFiltersOpen(true)}
                            >
                                <AdjustmentsHorizontalIcon className="h-5 w-5" />
                                <span className="text-sm font-medium">Analysis Controls</span>
                            </button>
                        </div>
                    </div>

                    {/* Chart and Results Content */}
                    <div className="px-4 py-6 sm:px-6 lg:px-8">
                        {gamesData ? (
                            <div className="space-y-6">
                                {/* Results Overview Section */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-800/30 p-4">
                                    <ResultsOverview 
                                        resultSets={results} 
                                        strNumGames={`L${numGames}`} 
                                        changeNumGames={changeNumGames} 
                                        apiError={apiError}
                                    />
                                </div>
                                {/* Chart Section */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-800/30 p-4">
                                    <GameAnalyzerChart 
                                        chartType={chartType} 
                                        data={chartData} 
                                        options={chartOptions} 
                                        chartLabel={chartLabel} 
                                        horizontalLine={horizontalLine}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-64 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-800/30">
                                {apiError ? (
                                    <span className="text-gray-500 dark:text-gray-400">Data Unavailable</span>
                                ) : (
                                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-red-500 border-t-transparent"></div>
                                )}
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default GameAnalyzer;